import { ArrowLeft, Lock } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/features/auth/store/auth.store";
import {
	getPublicProjectPreview,
	type PublicProjectSummary,
} from "@/features/marketplace/services/marketplace-api.service";

export function ExploreProjectPreviewPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const { id } = useParams();
	const { isAuthenticated } = useAuthStore();
	const [project, setProject] = useState<PublicProjectSummary | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [showAuthPrompt, setShowAuthPrompt] = useState(false);
	const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

	// A visit that began on this page has no earlier entry to return to, so it
	// falls back to the public list; any other visit goes back to where it came
	// from, whichever page that was.
	function handleBack() {
		if (location.key === "default") {
			navigate("/public-projects");
			return;
		}
		navigate(-1);
	}

	function getImagePairs(images: string[]) {
		const pairs: Array<{ before: string | null; after: string | null }> = [];
		for (let i = 0; i < images.length; i += 2) {
			pairs.push({
				before: images[i] || null,
				after: images[i + 1] || null,
			});
		}
		return pairs;
	}

	useEffect(() => {
		const load = async () => {
			setIsLoading(true);
			try {
				if (!id) {
					setProject(null);
					return;
				}
				const result = await getPublicProjectPreview(id);
				setProject(result);
			} finally {
				setIsLoading(false);
			}
		};
		load();
	}, [id]);

	function handleProtectedAction(action: "start" | "contact") {
		if (!isAuthenticated()) {
			setShowAuthPrompt(true);
			return;
		}

		if (action === "start") {
			navigate("/projects/new");
			return;
		}
		navigate("/designers");
	}

	if (isLoading) {
		return (
			<div className="mx-auto w-full max-w-4xl space-y-6 p-4 sm:p-6">
				<Skeleton className="h-8 w-24" />
				<Skeleton className="h-64 w-full rounded-lg" />
				<Skeleton className="h-32 w-full rounded-lg" />
			</div>
		);
	}

	if (!project) {
		return (
			<div className="mx-auto min-h-screen w-full max-w-3xl space-y-4 p-6">
				<Button variant="ghost" size="sm" onClick={handleBack}>
					<ArrowLeft className="mr-2 h-4 w-4" />
					Back
				</Button>
				<Card>
					<CardContent className="p-6">
						<h1 className="text-xl font-semibold text-foreground">
							Project not found
						</h1>
						<p className="mt-2 text-sm text-muted-foreground">
							The project preview is unavailable.
						</p>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="mx-auto min-h-screen w-full max-w-4xl space-y-6 p-6">
			<Button variant="ghost" size="sm" onClick={handleBack}>
				<ArrowLeft className="mr-2 h-4 w-4" />
				Back
			</Button>

			<div className="space-y-4">
				{(project.previewImages.length > 0
					? getImagePairs(project.previewImages)
					: [{ before: null, after: null }]
				).map((pair, pairIndex) => (
					<div
						key={`${pair.before ?? "before-empty"}-${pair.after ?? "after-empty"}`}
						className="space-y-2"
					>
						<p className="text-xs font-medium text-muted-foreground">
							Before / After {pairIndex + 1}
						</p>
						<div className="grid gap-4 sm:grid-cols-2">
							{pair.before ? (
								<button
									type="button"
									aria-label={t("explorePreview.openPhoto", {
										index: pairIndex * 2 + 1,
										total: project.previewImages.length,
									})}
									onClick={() => setLightboxIndex(pairIndex * 2)}
									className="cursor-zoom-in rounded-lg border-0 bg-transparent p-0"
								>
									<img
										src={pair.before}
										alt={`${project.title} before ${pairIndex + 1}`}
										className="h-56 w-full rounded-lg object-cover"
									/>
								</button>
							) : (
								<div className="rounded-lg bg-muted p-14 text-center text-sm text-muted-foreground">
									Before
								</div>
							)}
							{pair.after ? (
								<button
									type="button"
									aria-label={t("explorePreview.openPhoto", {
										index: pairIndex * 2 + 2,
										total: project.previewImages.length,
									})}
									onClick={() => setLightboxIndex(pairIndex * 2 + 1)}
									className="cursor-zoom-in rounded-lg border-0 bg-transparent p-0"
								>
									<img
										src={pair.after}
										alt={`${project.title} after ${pairIndex + 1}`}
										className="h-56 w-full rounded-lg object-cover"
									/>
								</button>
							) : (
								<div className="rounded-lg bg-primary/10 p-14 text-center text-sm text-primary">
									After
								</div>
							)}
						</div>
					</div>
				))}
			</div>

			<div>
				<h1 className="text-2xl font-bold text-foreground">{project.title}</h1>
				<p className="mt-2 text-sm text-muted-foreground">
					{project.description ||
						"A curated home transformation from the Finishd community."}
				</p>
				<p className="mt-2 text-sm text-muted-foreground">
					Professional:{" "}
					{project.designerName || "Featured Finishd professional"}
				</p>
				{project.projectCity && (
					<p className="mt-1 text-xs text-muted-foreground">
						Location: {project.projectCity}
					</p>
				)}
			</div>

			<div className="flex flex-wrap gap-3">
				<Button onClick={() => handleProtectedAction("start")}>
					Start a similar project
				</Button>
				<Button
					variant="outline"
					onClick={() => handleProtectedAction("contact")}
				>
					Contact professional
				</Button>
			</div>

			<ImageLightbox
				images={project.previewImages.map((src, index) => ({
					src,
					alt: `${project.title} ${index % 2 === 0 ? "before" : "after"} ${Math.floor(index / 2) + 1}`,
				}))}
				openIndex={lightboxIndex}
				onOpenIndexChange={setLightboxIndex}
			/>

			{showAuthPrompt && (
				<Card className="border-primary/40">
					<CardContent className="flex items-start gap-3 p-4">
						<Lock className="mt-0.5 h-4 w-4 text-primary" />
						<div>
							<p className="text-sm text-foreground">
								Please sign up or log in to continue.
							</p>
							<Button
								className="mt-3"
								size="sm"
								onClick={() => navigate("/login")}
							>
								Sign up / Log in
							</Button>
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
}
